import React, { useEffect, useRef, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { useDebounce } from "../../../hooks/useDebounce";
import { highlightMatch } from "../../../lib/highlightMatch";
import { useAutocompleteNavigation } from "../../../hooks/useAutocompleteNavigation";

export function AutocompleteZipcode({
  item,
  value,
  error = false,
  helperText,
  disabled = false,
  onFieldChange,
  supplementAddressData,
  className,
  minLength = 3,
}) {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");

  const inputRef = useRef(null);

  const debouncedQuery = useDebounce(inputValue || "", 500);

  useEffect(() => {
    if (!debouncedQuery.trim()|| debouncedQuery.trim().length < minLength) {
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      setSearchQuery("");
      return;
    }

    setIsLoading(true);
    setSearchQuery(debouncedQuery);
    item?.apiFunction?.mutate(debouncedQuery, {
      onSuccess: (response) => {
        // const results = response?.data?.suggestions || [];
        let newOptions = [];
        const zipSuggested = response?.data?.[0]?.city_states || [];
        const zipcodes = response?.data?.[0]?.zipcodes || [];
        if (zipSuggested && zipcodes) {
          const zipCode = zipcodes[0]?.zipcode;
          const temp = zipSuggested.map((ele) => {
            return { ...ele, ...{ zipCode } };
          });
          newOptions = [...newOptions, ...temp];
        }
        setSuggestions(newOptions);
        setIsOpen(newOptions?.length > 0);
        setSelectedIndex(-1);
        setIsLoading(false);
      },
      onError: (err) => {
        console.error("Error fetching suggestions:", err);
        setSuggestions([]);
        setIsOpen(false);
        setIsLoading(false);
      },
    });
  }, [debouncedQuery]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    const isEmptyValue = newValue.trim() === "";
    const addressValue = {
      state: isEmptyValue ? "" : supplementAddressData?.state || "",
      city: isEmptyValue ? "" : supplementAddressData?.city || "",
      zipCode: newValue || "",
    };
    onFieldChange(item.pathToValue, addressValue, item.type, item);
    if (newValue.trim()) {
      setIsLoading(true);
    }
  };

  const handleClear = () => {
    onFieldChange(
      item.pathToValue,
      { state: "", city: "", zipcode: "" },
      item.type,
      item
    );
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    setSearchQuery("");
    inputRef.current?.focus();
  };

  const handleSelect = (suggestion) => {
    onFieldChange(item.pathToValue, suggestion, item.type, item);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const { handleKeyDown, suggestionRefs, containerRef } =
    useAutocompleteNavigation({
      isOpen,
      setIsOpen,
      suggestions,
      selectedIndex,
      setSelectedIndex,
      onSelect: handleSelect,
    });

  const displayValue = typeof value === "string" ? value : value || "";

  return (
    <div className="space-y-2">
      <div ref={containerRef} className="relative w-full">
        <div className="relative">
          {/* Search Icon */}
          <Search
            className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 pointer-events-none ${
              disabled ? "text-gray-300" : "text-gray-400"
            }`}
          />

          {/* Input Field */}
          <input
            ref={inputRef}
            id={item?.pathToValue}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={item?.placeholder || "Search for street address"}
            disabled={item?.disabled || disabled}
            autoComplete="off"
            className={`w-full rounded-lg border py-2 pl-10 pr-10 text-sm transition-colors focus:outline-none focus:ring-2 ${
              error
                ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                : disabled
                ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            } ${className || ""}`}
            role="combobox"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-invalid={error ? "true" : "false"}
            aria-describedby={
              helperText || error ? `${item?.pathToValue}-helper` : undefined
            }
          />

          {/* Loading Spinner or Clear Button */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {isLoading && !disabled ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            ) : value && !disabled ? (
              <button
                onClick={handleClear}
                className="rounded p-0.5 text-gray-400 transition-colors hover:text-gray-600"
                aria-label="Clear input"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && suggestions.length > 0 && !disabled && (
          <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-300 bg-white shadow-lg">
            <ul role="listbox" className="py-1">
              {suggestions.map((suggestion, index) => {
                const fullAddress = `${suggestion.zipCode}, ${suggestion.city}, ${suggestion.state}`;

                return (
                  <li
                    key={`${suggestion.street_line}-${index}`}
                    ref={(el) => (suggestionRefs.current[index] = el)}
                    role="option"
                    aria-selected={selectedIndex === index}
                    className={`cursor-pointer px-3 py-2 transition-colors ${
                      selectedIndex === index
                        ? "bg-[#F7F6F3] text-gray-900"
                        : "text-gray-700 hover:bg-[#F7F6F3]"
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(suggestion);
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="text-gray-900 text-base leading-relaxed">
                      {highlightMatch(fullAddress, searchQuery)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
